import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { useDispatch } from 'react-redux';
import { loginSuccess } from '../store/userSlice';
import axiosInstance from '../api/axiosInstance';
import LoadingSpinner from './LoadingSpinner';
import { useNavigate, useLocation } from 'react-router-dom';

const Login = ({ switchToRegister, switchToForgotPassword, onSuccess, setIsLoading, isLoading }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Login user
      const response = await axiosInstance.post('/auth/login', formData);
      
      if (response.data.token) {
        const token = response.data.token;
        localStorage.setItem('token', token);
        
        // Fetch user data
        const userResponse = await axiosInstance.get('/users/getMe');
        
        const userData = {
          ...userResponse.data.data,
          addresses: userResponse.data.data.addresses || [],
          orders: userResponse.data.data.orders || [],
        };

        dispatch(loginSuccess({
          token: token,
          user: userData
        }));

        if (onSuccess) {
          onSuccess({
            token: token,
            user: userData
          });
        }

        // Check if we need to redirect back to cart
        const from = location.state?.from || '/';
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      // Display error message without redirecting
      setError(error.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
      <div className="bg-white py-8 px-4 rounded-2xl border border-sand shadow-card sm:px-10">
        <form className="space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="text-red-600 text-sm mb-4">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="email" className="block text-xs font-medium uppercase tracking-wide text-clay">
              Email address
            </label>
            <div className="mt-1">
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border border-sand rounded-xl shadow-sm placeholder-clay focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-medium uppercase tracking-wide text-clay">
              Password
            </label>
            <div className="mt-1">
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={formData.password}
                onChange={handleChange}
                className="appearance-none block w-full px-3 py-2 border border-sand rounded-xl shadow-sm placeholder-clay focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 sm:text-sm"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm">
              <button
                type="button"
                onClick={switchToForgotPassword}
                className="font-medium text-brand-600 hover:text-brand-700"
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium bg-ink text-cream hover:bg-brand-500 hover:text-ink transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
            >
              {isLoading ? <LoadingSpinner /> : 'Sign in'}
            </button>
          </div>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-sand" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-clay">Or</span>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={switchToRegister || (() => navigate('/signup'))}
              className="w-full inline-flex justify-center py-2 px-4 border border-sand rounded-xl shadow-sm bg-white text-sm font-medium text-clay hover:bg-cream transition-colors"
            >
              Create new account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
